import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsPage() {
  const navigate = useNavigate();
  const appName = 'Diamond Scorer';
  const companyName = 'Diamond Scorer';
  const effectiveDate = 'February 1, 2026';

  return (
    <div className="min-h-screen bg-stone-900 text-stone-300 p-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" className="text-stone-400 mb-4" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>

        <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Terms of Use</h1>
        <p className="text-stone-500 text-sm mb-6">Effective Date: {effectiveDate}</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-white font-bold text-base mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using {appName} ("the App"), you agree to be bound by these Terms of Use. If you do not agree to these terms, do not use the App. You must be at least 13 years old to create an account. If you are under 18, you represent that your parent or legal guardian has reviewed and agreed to these Terms on your behalf.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">2. Description of Service</h2>
            <p>{appName} is a baseball and softball scoring, lineup management, and team communication application. The App provides tools for coaches, parents, and administrators to manage games, track statistics, communicate with team members, and view live game updates.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">3. Account Registration</h2>
            <p>To use certain features, you must create an account with a unique username and password. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and current information during registration.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">4. Device Permissions</h2>
            <p>The App may request access to certain device features to provide full functionality:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong className="text-white">Camera:</strong> Used to scan lineup cards and capture team photos. Camera access is only activated when you explicitly use the scan or photo features.</li>
              <li><strong className="text-white">Push Notifications:</strong> Used to deliver game updates, scoring alerts, and team messages. You may opt in or out at any time.</li>
              <li><strong className="text-white">Storage:</strong> Used to save photos and exported reports to your device.</li>
            </ul>
            <p className="mt-2">You may revoke any of these permissions at any time through your device settings. Some features may not function without the required permissions.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">5. User Content</h2>
            <p>You retain ownership of all content you submit through the App, including player names, game data, lineup information, photos, and messages. By submitting content, you grant {companyName} a non-exclusive, worldwide license to store, process, and display your content solely for the purpose of providing the App's services to you and your team.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Use the App for any unlawful purpose</li>
              <li>Share inappropriate, offensive, or harmful content</li>
              <li>Attempt to gain unauthorized access to other users' accounts</li>
              <li>Interfere with or disrupt the App's services</li>
              <li>Use the App to collect personal information about minors without parental consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">7. Children's Privacy (COPPA Compliance)</h2>
            <p>Children under 13 may not create an account. Coaches and parents who enter player information for children under 13 represent that they have obtained appropriate parental consent. We do not knowingly collect personal information directly from children under 13. If you believe a child under 13 has provided personal information without parental consent, please contact us immediately.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">8. Subscriptions and Payments</h2>
            <p>Certain premium features may require a paid subscription. Subscription terms, pricing, and billing cycles will be clearly presented before purchase. You may cancel your subscription at any time. Refunds are handled in accordance with the applicable app store's refund policies.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">9. Disclaimer of Warranties</h2>
            <p>The App is provided "as is" without warranties of any kind, either express or implied. {companyName} does not guarantee that the App will be uninterrupted, error-free, or free of harmful components. Game statistics and scoring data are recorded as entered by users and should not be considered official records.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">10. Limitation of Liability</h2>
            <p>{companyName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the App, including but not limited to loss of data, game records, or statistics.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">11. Termination</h2>
            <p>We reserve the right to suspend or terminate your account at any time for violation of these Terms. You may delete your account at any time by contacting support.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">12. Changes to Terms</h2>
            <p>We may update these Terms from time to time. Continued use of the App after changes constitutes acceptance of the new Terms. We will notify users of material changes through the App or via email.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">13. Governing Law</h2>
            <p>These Terms are governed by and construed in accordance with the laws of the United States. Any disputes arising from these Terms shall be resolved in the appropriate courts.</p>
          </section>

          <section>
            <h2 className="text-white font-bold text-base mb-2">14. Contact</h2>
            <p>If you have questions about these Terms, please contact us through the App's support features.</p>
          </section>
        </div>

        <div className="border-t border-stone-700 mt-8 pt-4 text-center text-stone-600 text-xs">
          {companyName} &copy; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
